import { useRef, useState, useCallback, useMemo } from 'react';
import { useQuery } from 'hooks/useCustomQuery';
import { useSelector } from 'react-redux';
import { axios } from '@/services/axios';
import { Flex, Tooltip, HStack, Text, AlertTitle } from '@chakra-ui/react';
import { Alert, AlertIcon } from '@/components/v4';

import { modalTitle } from '../../../components/tables/TableAndModal';
import {
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from '@chakra-ui/react';
import IntRates from './rates/Index';
import config from '../../../config.json';
import { ModalCopy, StatusCellRenderer } from '../../../components';
import { Button } from 'components/v4';
import EditDetailsForm from './EditDetailsForm';

import AGGridMain from '../../../components/tables/AGGrid/AGGridMain';
import InternationalRateCreate from './Create';
import {
    customTabStylesCombineTheme,
    selectedTabStylesCombineTheme,
} from '@/constants';
import ActionMenu from 'components/presentational/ActionMenu';
import { EditIcon } from '@chakra-ui/icons';
import { CopyIcon } from 'components/v4/Icon/CopyIcon';
import BreadcrumbEntityDropdown from 'components/pickers/BreadcrumbEntityDropdown';

function InternationalRateHeader({ setInternationalRatesAction }) {
    return (
        <div style={{ paddingLeft: '20px' }}>
            <HStack spacing={2}>
                <Tooltip
                    label="Add International Rate"
                    placement="bottom"
                    hasArrow
                    portalProps={{ containerRef: undefined }}>
                    <div
                        style={{
                            position: 'relative',
                            display: 'inline-block',
                        }}>
                        {' '}
                        <Button
                            size="sm"
                            width="170px"
                            onClick={() =>
                                setInternationalRatesAction('Create')
                            }>
                            + Add International Rate
                        </Button>
                    </div>
                </Tooltip>
            </HStack>
        </div>
    );
}
/*
 * Component for the base page for the International Rates List Tab from the Sidebar, this also covers the add and edit pages in ModalContent
 */
const InternationalRatesList = () => {
    const tableRef = useRef();
    // Local state for child dashboard view
    const [childViewActive, setChildViewActive] = useState(false);
    const [selectedChild, setSelectedChild] = useState(null);
    const [parentList, setParentList] = useState([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const [internationalRatesAction, setInternationalRatesAction] =
        useState('Get');

    // Local state for modal operations
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState(''); // 'Edit' or 'Copy'
    const [modalData, setModalData] = useState(null);
    const [modalState, setModalState] = useState({});
    const [modalStateError, setModalStateError] = useState({});
    const [wholesalerOptions, setWholesalerOptions] = useState([]);
    const [partnerOptions, setPartnerOptions] = useState([]);

    // wholesaler/partners from state
    const { currentWholesaler, currentPartner } = useSelector((state) => {
        return { ...state.navigationLists };
    });

    // scope from state
    const { currentScope, requiredScope } = useSelector((state) => {
        return { ...state.login };
    });
    const { darkMode } = useSelector((state) => state.settings);
    // function to determine endpoint to use for the table fetch based on scope
    const tableFetch = () => {
        switch (currentScope) {
            case 80:
                return '/internationalratelists/';
            case 60:
                return `/internationalratelists/wholesaler/${currentWholesaler}/?withPublic=true`;
            default:
                return `/internationalratelists/partner/${currentPartner}/?withPublic=true`;
        }
    };

    // Table data fetch using TanStack useQuery
    const {
        data: tableData = [],
        isLoading: tableLoading,
        isFetching: tableFetching,
        error: tableErrorObj,
        refetch,
    } = useQuery({
        queryKey: [
            'internationalRateTable',
            currentScope,
            internationalRatesAction,
        ],
        queryFn: async () => {
            const res = await axios.get(tableFetch());
            const data = res.data || [];
            return data.map(({ wholesaler, partner, ...rest }) => ({
                ...rest,
                wholesaler:
                    wholesaler?.length > 0
                        ? wholesaler
                        : config.notAllocated.table,
                partner:
                    partner?.length > 0 ? partner : config.notAllocated.table,
            }));
        },
        keepPreviousData: true,
    });

    const { refetch: refetchWholesalers } = useQuery({
        queryKey: ['intlRateWholesalersList'],
        queryFn: async () => {
            const wholesalerRes = await axios.get('/wholesalers/');
            return (wholesalerRes.data || []).map((w) => ({
                label: w.name,
                value: w.id,
            }));
        },
        enabled: false,
        onSuccess: (data) => setWholesalerOptions(data),
    });

    // Action handlers
    const handleEdit = useCallback(
        async (params) => {
            const rowData = params.data;
            setChildViewActive(true);
            setParentList(tableData);
            setSelectedTab(0);

            let selectedWholesalerID =
                rowData.wholesalerID || rowData.wholesaler?.id || null;
            let selectedPartnerID =
                rowData.partnerID || rowData.partner?.id || null;

            setSelectedChild({
                ...rowData,
                wholesalerID: selectedWholesalerID,
                partnerID: selectedPartnerID,
            });
            await refetchWholesalers();
        },
        [refetchWholesalers, tableData],
    );

    const handleCopy = useCallback((params) => {
        const rowData = params.data;
        setModalOpen(true);
        setModalMode('Copy');
        setModalData(rowData);
        setModalState({
            copyName: `Copy of ${rowData?.name}`,
            wholesalerID:
                rowData.wholesalerID || rowData.wholesaler?.id || null,
            partnerID: rowData.partnerID || rowData.partner?.id || null,
            isActive: rowData.isActive,
            isPublic: rowData.isPublic,
        });
        setModalStateError({});
    }, []);

    const baseColumns = useMemo(
        () => [
            {
                headerName: 'Wholesaler',
                field: 'wholesaler',
                hide: currentScope !== 80,
            },
            {
                headerName: 'Partner',
                field: 'partner',
                hide: currentScope === 40,
            },
            { headerName: 'Name', field: 'name', sortable: true },
            {
                headerName: 'Public',
                field: 'isPublic',
                width: 100,
                cellStyle: { textAlign: 'center' },
                cellRenderer: ({ value }) => (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                        }}>
                        {value ? (
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M5 10l3 3 7-7"
                                    stroke="#38A169"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    fill="none"
                                />
                            </svg>
                        ) : (
                            <span
                                style={{
                                    fontSize: '1.5rem',
                                    color: '#A0AEC0',
                                }}>
                                -
                            </span>
                        )}
                    </div>
                ),
            },
            {
                headerName: 'Active',
                field: 'isActive',
                width: 100,
                cellRenderer: StatusCellRenderer,
                filter: 'agSetColumnFilter',
                filterParams: {
                    valueFormatter: (params) => {
                        if (params.value === true || params.value === 'true')
                            return 'Active';
                        if (params.value === false || params.value === 'false')
                            return 'Inactive';
                        return '';
                    },
                },
            },
            {
                lockPosition: 'right',
                headerName: 'Action',
                maxWidth: 100,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                cellRenderer: (params) => {
                    const actionItems = [
                        {
                            label: 'Edit',
                            onClick: () => handleEdit(params),
                            icon: EditIcon,
                        },
                        {
                            label: 'Copy',
                            onClick: () => handleCopy(params),
                            icon: CopyIcon,
                        },
                    ];

                    return <ActionMenu actionItems={actionItems} />;
                },
            },
        ],
        [currentScope, handleEdit, handleCopy],
    );

    const tableDataWithColumns = useMemo(
        () => ({ columns: baseColumns, data: tableData }),
        [baseColumns, tableData],
    );

    const tableError = tableErrorObj ? 'Error loading table data' : '';

    // Check if entity is selected - if not, show alert
    const hasRequiredEntity =
        (currentScope === 40 && currentPartner) ||
        (currentScope === 60 && currentWholesaler) ||
        currentScope === 80; // Global scope doesn't require selection

    if (!hasRequiredEntity) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>
                    No Partner / Company / Wholesaler Selected
                </AlertTitle>
                <Text>Please select a wholesaler and a partner / company.</Text>
            </Alert>
        );
    }

    // Modal validation logic
    const modalValidate = () => {
        let newErrorState = {};
        if (modalMode === 'Copy') {
            newErrorState = {
                copyName:
                    (!modalState?.copyName ||
                        modalState?.copyName?.length < 1 ||
                        modalState?.copyName?.length > 100) &&
                    'Field is required and maximum field length is 100 characters.',
            };
            setModalStateError(newErrorState);
            return !newErrorState.copyName;
        }
        newErrorState = {
            name:
                (!modalState?.name ||
                    modalState?.name?.length < 1 ||
                    modalState?.name?.length > 100) &&
                'Field is required and maximum field length is 100 characters.',
        };
        setModalStateError(newErrorState);
        return !newErrorState.name;
    };

    // If childViewActive, render breadcrumbs and tabs view
    if (childViewActive && selectedChild && parentList.length > 0) {
        return (
            <>
                {/* Simple breadcrumb navigation */}
                <nav
                    style={{
                        marginBottom: 16,
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                    <span
                        style={{
                            fontWeight: 'bold',
                            marginRight: 8,
                            cursor: 'pointer',
                            color: '#3182ce',
                        }}
                        onClick={() => setChildViewActive(false)}>
                        International Rate Lists
                    </span>
                    <span
                        style={{
                            margin: '0 8px',
                            fontFamily: 'Inter',
                            fontSize: '14px',
                            fontWeight: 'normal',
                            color: darkMode ? '#FFFFFF' : '#262626',
                        }}>
                        &gt;
                    </span>
                    <BreadcrumbEntityDropdown
                        items={parentList}
                        selectedId={selectedChild?.id}
                        onSelect={(childData) =>
                            setSelectedChild({
                                ...childData,
                                wholesalerID:
                                    childData.wholesalerID ||
                                    childData.wholesaler?.id ||
                                    null,
                                partnerID:
                                    childData.partnerID ||
                                    childData.partner?.id ||
                                    null,
                            })
                        }
                        darkMode={darkMode}
                        placeholder="Select"
                        width="380px"
                    />
                </nav>
                {/* Tabs UI for child dashboard */}
                <Tabs
                    key={selectedChild?.id}
                    index={selectedTab}
                    onChange={setSelectedTab}
                    isLazy>
                    <TabList borderBottom="none" mb={2}>
                        <Tab
                            sx={{
                                ...customTabStylesCombineTheme,
                                color: darkMode
                                    ? 'rgba(255, 255, 255)'
                                    : 'inherit',
                            }}
                            _selected={{
                                ...selectedTabStylesCombineTheme,
                            }}>
                            Edit Details
                        </Tab>
                        <Tab
                            sx={{
                                ...customTabStylesCombineTheme,
                                color: darkMode
                                    ? 'rgba(255, 255, 255)'
                                    : 'inherit',
                            }}
                            _selected={{
                                ...selectedTabStylesCombineTheme,
                            }}>
                            International Rates
                        </Tab>
                    </TabList>
                    <TabPanels>
                        <TabPanel padding={0}>
                            {/* Edit form for selectedChild - FormBase style */}
                            <EditDetailsForm
                                selectedChild={selectedChild}
                                darkMode={darkMode}
                                requiredScope={requiredScope}
                                wholesalerOptions={wholesalerOptions}
                                partnerOptions={partnerOptions}
                                setPartnerOptions={setPartnerOptions}
                                onBack={() => setChildViewActive(false)}
                                onSubmit={async (values) => {
                                    await axios.put('/internationalratelist', {
                                        name: values.name,
                                        wholesalerID: values.wholesalerID,
                                        wholesaler: values.wholesaler,
                                        partnerID: values.partnerID,
                                        partner: values.partner,
                                        isActive: values.isActive,
                                        isPublic: values.isPublic,
                                        id: selectedChild.id,
                                    });
                                    // Refresh table data
                                    await refetch();
                                }}
                            />
                        </TabPanel>
                        <TabPanel padding={0}>
                            {/* International Rates dashboard view - pass child id as prop */}
                            <IntRates childId={selectedChild?.id} />
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </>
        );
    }
    // Otherwise, render parent table view
    return (
        <>
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    marginBottom: '15px',
                }}>
                International Rate List
            </Text>
            {internationalRatesAction === 'Get' && (
                <AGGridMain
                    gridRef={tableRef}
                    tableData={tableDataWithColumns}
                    titleName="International Rate List"
                    refetch={refetch}
                    isLoading={tableLoading}
                    isFetching={tableFetching}
                    isSaveButtonEnabled
                    extraComponents={
                        <Flex gap="4">
                            <InternationalRateHeader
                                setInternationalRatesAction={
                                    setInternationalRatesAction
                                }
                            />
                        </Flex>
                    }
                />
            )}
            {internationalRatesAction === 'Create' && (
                <InternationalRateCreate
                    setInternationalRatesAction={setInternationalRatesAction}
                    childId={selectedChild?.id}
                    currentPartner={currentPartner}
                    currentWholesaler={currentWholesaler}
                    currentScope={currentScope}
                    //setSuccessAction={setSuccessAction}
                />
            )}
            {tableError && (
                <div style={{ color: 'red', margin: 8 }}>{tableError}</div>
            )}

            {/* Modal UI: Open for Edit or Copy mode */}
            {modalOpen && (
                <Modal
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    isCentered
                    size="xl">
                    <ModalOverlay />
                    <ModalContent
                        style={{
                            background: darkMode ? '#2e2e30' : 'white',
                            color: darkMode ? 'white' : 'black',
                        }}>
                        <ModalHeader
                            style={{
                                borderBottom: '1px solid',
                                borderColor: darkMode ? '#444950' : '#E2E8F0',
                            }}>
                            {modalTitle(
                                modalData,
                                'name',
                                ' International Rate List ',
                                modalMode,
                            )}
                        </ModalHeader>
                        <ModalBody>
                            <ModalCopy
                                modalData={modalData}
                                modalState={modalState}
                                modalStateError={modalStateError}
                                setModalState={(payload) =>
                                    setModalState({ ...modalState, ...payload })
                                }
                            />
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                variant="outline"
                                mr={3}
                                onClick={() => setModalOpen(false)}>
                                Close
                            </Button>
                            <Button
                                colorScheme="blue"
                                onClick={async () => {
                                    if (!modalValidate()) return;
                                    try {
                                        const copyRes = await axios.post(
                                            `/internationalratelist/${modalData.id}/copy`,
                                            {
                                                name: modalState.copyName,
                                                wholesalerID:
                                                    modalState.wholesalerID,
                                                wholesaler:
                                                    modalState.wholesaler,
                                                partnerID: modalState.partnerID,
                                                partner: modalState.partner,
                                                isActive: modalState.isActive,
                                                isPublic: modalState.isPublic,
                                            },
                                        );
                                        setModalOpen(false);
                                        await refetchWholesalers();
                                        // Refresh table data
                                        await refetch();

                                        // Find the new copied item in the table
                                        let copiedItem = null;
                                        if (copyRes?.data?.id) {
                                            copiedItem = tableData.find(
                                                (item) =>
                                                    item.id === copyRes.data.id,
                                            );
                                        }
                                        // Open child tabs view in edit mode with copied item selected
                                        if (copiedItem) {
                                            setChildViewActive(true);
                                            setParentList(tableData);
                                            setSelectedTab(0);
                                            setSelectedChild({
                                                ...copiedItem,
                                                wholesalerID:
                                                    copiedItem.wholesalerID ||
                                                    copiedItem.wholesaler?.id ||
                                                    modalState.wholesalerID,
                                                partnerID:
                                                    copiedItem.partnerID ||
                                                    copiedItem.partner?.id ||
                                                    modalState.partnerID,
                                            });
                                        }
                                    } catch (err) {
                                        setModalStateError({
                                            copyName:
                                                err?.response?.data?.message ||
                                                'Error copying rate list',
                                        });
                                    }
                                }}>
                                Copy
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
        </>
    );
};

export default InternationalRatesList;
